import { Box, Divider, Typography } from '@mui/material';
import { useState } from 'react';
import { addMyFleetVessels } from '../../../api/my-fleet';
import {
  AddMyFleetVesselsResponse,
  MAX_ALERT_CONCURRENCY_SIZE,
  MAX_ALERT_CREATION_BATCH_SIZE,
  MAX_VESSEL_CONCURRENCY_SIZE,
  MAX_VESSEL_CREATION_BATCH_SIZE,
  MyFleetDialogStatus,
  MyFleetVessels,
} from '../../../models/my-fleet.model';

import createAlertsForVessels from '../../../api/alert-batch-create';
import useUserRoleAndShortId from '../../../hooks/user-details/useUserDetails';
import { ProAlertCreateResponse } from '../../../models/pro.model';
import {
  getCountAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../utils/analytics';

import batchCreateVessels from '../../../api/vessel-batch';
import useAccessControl from '../../../hooks/access-control/useAccessControl';
import MyFleetCsvUploadButton from './my-fleet-add-vessels-csv/my-fleet-add-vessels-csv';
import MyFleetCsvTemplateDownload from './my-fleet-add-vessels-csv/my-fleet-csv-tetmplate-download';
import MyFleetVesselsCreateDialog from './my-fleet-add-vessels-dialog/my-fleet-add-vessels-dialog';
import MyFleetAddVesselsManualInput from './my-fleet-add-vessels-manual-input/my-fleet-add-vessels-manual-input';
import IrisVesselLinkButton from './my-fleet-iris-link/iris-vessel-link-button';

interface MyFleetAddVesselsContainerProps {
  onAddSuccess?: () => void;
  myFleetVessels: MyFleetVessels | null;
}

function MyFleetAddVesselsContainer({
  onAddSuccess,
  myFleetVessels,
}: MyFleetAddVesselsContainerProps) {
  const { canAccessFeatureIrisVesselsLink } = useAccessControl();
  const [imoInput, setImoInput] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const userDetails = useUserRoleAndShortId();
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);
  const [status, setStatus] = useState<MyFleetDialogStatus>(
    MyFleetDialogStatus.Idle
  );
  const [addVesselResponse, setAddVesselResponse] =
    useState<AddMyFleetVesselsResponse | null>(null);
  const [addAlertsResponse, setAddAlertsResponse] =
    useState<ProAlertCreateResponse | null>(null);
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
    completed: number;
    failed: number;
  }>({ current: 0, total: 0, completed: 0, failed: 0 });
  const [batchErrors, setBatchErrors] = useState<any[]>([]);

  const handleManualInputSubmit = async (imos: string[]) => {
    setStatus(MyFleetDialogStatus.CreatingVessels);
    setAddVesselResponse(null);
    setBatchProgress({
      current: 0,
      total: imos.length,
      completed: 0,
      failed: 0,
    });
    openDialog();

    const vesselsToAdd: MyFleetVessels = imos.map((imo) => ({
      imo,
    }));

    try {
      const response = await addMyFleetVessels(vesselsToAdd);
      setStatus(MyFleetDialogStatus.Success);
      setAddVesselResponse(response);
      if (response.vessels && response.vessels.length > 0) {
        logAnalyticsEvent('MyFleet', 'VesselsCreated', [
          getUserAnalyticsEventLabel(userDetails),
          getCountAnalyticsEventLabel(response.vessels.length),
        ]);
      }
    } catch (error: any) {
      setStatus(MyFleetDialogStatus.VesselError);
      if (error.response?.data.status_code === 400) {
        setAddVesselResponse(error.response.data);
      } else {
        setAddVesselResponse({
          message: 'An error occurred during vessel creation.',
        });
      }
    }
  };

  const handleBatchSubmit = async (
    vessels: { imo: number; emails: string[] }[]
  ) => {
    setStatus(MyFleetDialogStatus.CreatingVessels);
    setAddVesselResponse(null);
    setBatchProgress({
      current: 0,
      total: vessels.length,
      completed: 0,
      failed: 0,
    });
    setBatchErrors([]);
    openDialog();

    let vesselResponse;
    try {
      vesselResponse = await batchCreateVessels({
        vessels,
        batchSize: MAX_VESSEL_CREATION_BATCH_SIZE,
        concurrency: MAX_VESSEL_CONCURRENCY_SIZE,
        setProgress: ({ completed, total, failed }) => {
          setBatchProgress((prev) => ({ ...prev, completed, total, failed }));
        },
        setErrors: (errors) => {
          setBatchErrors(errors);
        },
        setResponse: (response) => {
          setAddVesselResponse(response);
        },
      });

      setAddVesselResponse(vesselResponse);
      setStatus(
        vesselResponse.errors && vesselResponse.errors.length > 0
          ? MyFleetDialogStatus.VesselError
          : MyFleetDialogStatus.Success
      );
    } catch (error: any) {
      setStatus(MyFleetDialogStatus.VesselError);
      setAddVesselResponse({
        message: error?.message || 'An error occurred during vessel creation.',
        errors: [error?.errors],
      });
      setAddAlertsResponse(null);
      return;
    }

    // Only proceed to alerts if there were no errors
    if (!vesselResponse.errors || vesselResponse.errors.length === 0) {
      const createdVessels = vesselResponse.vessels || [];
      const vesselsWithEmails = vessels.filter((v) => v.emails.length > 0);
      if (createdVessels.length > 0 && vesselsWithEmails.length > 0) {
        try {
          setStatus(MyFleetDialogStatus.CreatingAlerts);
          setBatchProgress({
            current: 0,
            total: vesselsWithEmails.length,
            completed: 0,
            failed: 0,
          });
          await createAlertsForVessels({
            vesselsWithEmails,
            allCreatedVessels: createdVessels,
            batchSize: MAX_ALERT_CREATION_BATCH_SIZE,
            concurrency: MAX_ALERT_CONCURRENCY_SIZE,
            setProgress: ({ completed, total, failed }) => {
              setBatchProgress((prev) => ({
                ...prev,
                completed,
                total,
                failed,
              }));
            },
            setResponse: (alertsResponse) => {
              setAddAlertsResponse(alertsResponse);
            },
          });
          setStatus(MyFleetDialogStatus.Success);
        } catch (alertError: any) {
          setAddAlertsResponse({
            message: 'An error occurred during alert creation.',
          });
          setStatus(MyFleetDialogStatus.AlertError);
        }
      } else {
        setAddAlertsResponse(null);
      }
    }
  };

  const handleDialogClose = () => {
    closeDialog();
    if (onAddSuccess && status === MyFleetDialogStatus.Success) onAddSuccess();
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', padding: '12px 20px' }}
    >
      <MyFleetAddVesselsManualInput
        value={imoInput}
        onChange={setImoInput}
        onSubmit={handleManualInputSubmit}
      />
      <Divider sx={{ margin: '1rem 0' }} />
      <Typography sx={{ marginBottom: '0.5rem', textAlign: 'left' }}>
        Upload Vessel(s)/Fleet
      </Typography>
      <MyFleetCsvTemplateDownload />
      {canAccessFeatureIrisVesselsLink.enabled && <IrisVesselLinkButton />}
      <MyFleetCsvUploadButton onBatchSubmit={handleBatchSubmit} />
      <MyFleetVesselsCreateDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        status={status}
        response={addVesselResponse}
        alertsResponse={addAlertsResponse}
        vessels={myFleetVessels}
        batchProgress={batchProgress}
        batchErrors={batchErrors}
      />
    </Box>
  );
}

export default MyFleetAddVesselsContainer;

MyFleetAddVesselsContainer.defaultProps = {
  onAddSuccess: undefined,
};
